import { clsx } from 'clsx';
import { useController } from 'react-hook-form';
import { Duration } from 'date-fns';
import { z } from 'zod';
import { InputErrorHelper, InputHelperText, InputLabel } from '@nexelem/inputs';
import { TextField, TextInputSize, VirtualKeyboardPopperEnable } from '@nexelem/components';
import { FormInputWrapper, FormInputWrapperProps } from '../../atoms/FormInputWrapper';
import { useCustomTranslation } from '../../../hooks';

export const formDurationInputSchema = z.object({
  hours: z.number(),
  minutes: z.number(),
  days: z.number().optional(),
});

export type FormDurationInputSchema = z.infer<typeof formDurationInputSchema>;

const DURATION_KEYS = ['hours', 'minutes'] satisfies Array<keyof Duration>;

type FormDurationInputProps = {
  name: string;
  label?: string;
  required?: boolean;
  labelClassName?: string;
  maxWidth?: FormInputWrapperProps['maxWidth'];
  isJumbo?: boolean;
  durationKeys?: Array<keyof Duration>;
  size?: TextInputSize;
  enableVirtualKeyboard?: VirtualKeyboardPopperEnable;
};

type FieldValue = {
  [key: string]: Duration | undefined;
};

export const FormDurationInput = ({
  label,
  maxWidth = 224,
  name,
  durationKeys,
  isJumbo,
  size = 'default',
  enableVirtualKeyboard,
  labelClassName,
  ...props
}: FormDurationInputProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const { tWrapper: tTimeUnits } = useCustomTranslation('components.inputDateWithDifference.units');
  const { field, fieldState } = useController<FieldValue>({ name });

  const internalLabel = label ?? tWrapper('productionDuration');

  const value = field.value || {};
  const errorMessage = fieldState.error?.message;

  const getHandleChange =
    (fieldName: keyof Duration) =>
    (newValue = '') => {
      const duration = { ...value, [fieldName]: Number(newValue) };
      field.onChange(duration);
    };

  const selectedDurationKeys = durationKeys || DURATION_KEYS;

  return (
    <FormInputWrapper fullWidth maxWidth={maxWidth}>
      <div className="mb-2">
        <InputLabel isJumbo={isJumbo} label={internalLabel} labelClassName={labelClassName} required={props.required} />
      </div>
      <div className="flex items-start gap-y-4">
        {selectedDurationKeys.map((key, index) => (
          <div className="flex" key={key}>
            <div className="flex flex-col gap-2">
              <TextField
                enableVirtualKeyboard={enableVirtualKeyboard}
                placeholder="00"
                showErrorBorder={!!errorMessage}
                size={size}
                type="number"
                value={value[key] === undefined ? '' : value[key] + ''}
                onChange={getHandleChange(key)}
              />
              <InputHelperText isJumbo={isJumbo} label={tTimeUnits(key)} />
            </div>
            {index !== selectedDurationKeys.length - 1 && (
              <div className={clsx(size === 'extraJumbo' && 'mt-8', size === 'jumbo' && 'mt-6', 'mx-2 mt-1')}>
                <span className="text-s16 md:text-s24">:</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {!!errorMessage && <InputErrorHelper errorMessage={errorMessage} />}
    </FormInputWrapper>
  );
};
